'use client';
// src/components/profile/RestaurantOwnerReplyForm.tsx
// Owner's right of reply on a diner review. The owner can publicly respond to
// any review — including a negative one — but can never delete or hide it.

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { MessageSquareReply, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { replyToRestaurantReviewAction } from '@/app/actions/restaurantReviews';

interface RestaurantOwnerReplyFormProps {
  profileId: string;
  reviewId: string;
  /** Existing reply text, when the owner has already responded. */
  existingReply?: string;
}

export function RestaurantOwnerReplyForm({
  profileId,
  reviewId,
  existingReply,
}: RestaurantOwnerReplyFormProps) {
  const router = useRouter();
  const [open, setOpen] = useState(!existingReply);
  const [body, setBody] = useState(existingReply || '');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (body.trim().length < 10) {
      setResult({ success: false, message: 'Please write at least 10 characters.' });
      return;
    }
    setSubmitting(true);
    setResult(null);
    const res = await replyToRestaurantReviewAction(profileId, reviewId, body.trim());
    setResult(res);
    setSubmitting(false);
    if (res.success) {
      setOpen(false);
      router.refresh();
    }
  }

  if (existingReply && !open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-orange-700 hover:underline"
      >
        <MessageSquareReply className="h-3.5 w-3.5" /> Edit your reply
      </button>
    );
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-orange-700 hover:underline"
      >
        <MessageSquareReply className="h-3.5 w-3.5" /> Reply to this review
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-2 space-y-2 rounded-lg border border-orange-200 bg-white p-3">
      <p className="text-xs font-semibold text-orange-800">
        Your public reply — you can respond to any review, but reviews cannot be deleted.
      </p>
      <Textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={500}
        rows={3}
        placeholder="Thank the diner, or explain what happened and how you're improving."
        required
      />
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={submitting}>
          {submitting ? 'Posting...' : existingReply ? 'Update reply' : 'Post reply'}
        </Button>
        {existingReply && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setBody(existingReply);
              setOpen(false);
              setResult(null);
            }}
          >
            Cancel
          </Button>
        )}
      </div>
      {result && !result.success && <p className="text-xs text-destructive">{result.message}</p>}
      {result?.success && (
        <p className="flex items-center gap-1 text-xs text-green-700">
          <CheckCircle2 className="h-3.5 w-3.5" /> {result.message}
        </p>
      )}
    </form>
  );
}
